import { supabase } from '../supabase';
import { Game, RecommendationFilters as SharedRecommendationFilters } from '../../types/index';
import { RECOMMENDATION_CONFIG } from './index';
import { BaseRecommender } from './baseRecommender';
import { DatabaseUtils, formatArrayForSupabase } from './utils/databaseUtils';
import { GameProcessingUtils } from './utils/gameProcessingUtils';

/**
 * Recomendador colaborativo que utiliza similaridade entre usuários
 * para encontrar jogos recomendados por usuários similares
 */
export class CollaborativeRecommender extends BaseRecommender {
  /**
   * Obtém jogos recomendados baseados em usuários similares
   */
  public async getRecommendations(
    userId: string,
    count: number,
    excludeGameIds: string[],
    filters?: SharedRecommendationFilters
  ): Promise<Game[]> {
    try {
      // Diretamente usa a tabela canônica
      const similarityTableInfo = {
        tableName: 'user_similarities',
        targetUserColumn: 'similar_user_id',
        scoreColumn: 'similarity_score'
      };

      // 1. Encontrar usuários similares
      const { data: similarUsers, error: similarUsersError } = await supabase
        .from(similarityTableInfo.tableName)
        .select(`${similarityTableInfo.targetUserColumn}, ${similarityTableInfo.scoreColumn}`)
        .eq('user_id', userId)
        .order(similarityTableInfo.scoreColumn, { ascending: false })
        .limit(RECOMMENDATION_CONFIG.recommenderThresholds.maxSimilarUsersToConsider); // Usar config

      if (similarUsersError || !similarUsers || similarUsers.length === 0) {
        console.warn('[Collab Recommender] Não foram encontrados usuários similares, usando fallback para populares.');
        return this.getFallbackRecommendations(count, excludeGameIds);
      }
      
      const similarUserIds = similarUsers.map((user: any) => 
        user[similarityTableInfo.targetUserColumn]
      ).filter(Boolean);
      
      if (similarUserIds.length === 0) {
        console.warn('[Collab Recommender] Não foi possível encontrar IDs de usuários similares válidos. Usando fallback.');
        return this.getFallbackRecommendations(count, excludeGameIds);
      }
      
      // Etapa Intermediária: Buscar APENAS IDs de jogos que usuários similares gostam e que não estejam na lista de exclusão inicial.
      // A query inicial já faz o join com 'games' mas não precisamos do objeto Game completo aqui.
      const { data: likedGameEntries, error: likedGameEntriesError } = await supabase
        .from('user_games')
        .select('game_id') // Apenas game_id
        .in('user_id', similarUserIds)
        .eq('owned', true) // Considerar jogos que o similar possui (ou outra lógica de interação positiva)
        .gte('rating', RECOMMENDATION_CONFIG.recommenderThresholds.minRatingForLiked) // Rating mínimo dado pelo similar
        .not('game_id', 'in', `(${excludeGameIds.join(',')})`); // Excluir jogos já passados na lista de exclusão

      if (likedGameEntriesError || !likedGameEntries || likedGameEntries.length === 0) {
        if (likedGameEntriesError) console.error('[Collab Recommender] Erro ao buscar IDs de jogos de usuários similares:', likedGameEntriesError);
        console.warn('[Collab Recommender] Nenhum jogo candidato encontrado de usuários similares. Usando fallback.');
        return this.getFallbackRecommendations(count, excludeGameIds);
      }

      const candidateGameIds = likedGameEntries
        .map((item: { game_id: string }) => item.game_id)
        .filter(Boolean); // Garante que não há nulos/undefined
      
      if (candidateGameIds.length === 0) {
        console.warn('[Collab Recommender] Lista de candidateGameIds vazia após mapeamento. Usando fallback.');
        return this.getFallbackRecommendations(count, excludeGameIds);
      }

      // Etapa Final: Usar DatabaseUtils.executeSafeQuery para buscar os jogos completos
      // e aplicar os filtros de complexidade, players, etc. (passados em 'filters'),
      // além de respeitar as exclusões globais e os excludeGameIds novamente (para garantir).
      // A ordenação pode ser por 'average_rating' ou outra métrica relevante.
      const recommendedGames = await DatabaseUtils.executeSafeQuery(
        {
          ...(filters || {}),
          // Passar excludeGameIds aqui garante que a lista final não os contenha,
          // mesmo que não tenham sido filtrados na query de candidateGameIds.
          // No entanto, a query de candidateGameIds já os exclui.
          // Se `excludeGameIds` for passado aqui, ele se aplicará ao resultado de `includeOnlyGameIds`.
          'excludeIds': excludeGameIds 
        },
        count * 2, // Buscar um pouco mais para ter margem para deduplicação e slice final
        'average_rating', // Ordenar por popularidade/avaliação
        false, // Descendente
        candidateGameIds // NOVO: Passar os IDs candidatos para o filtro .in()
      );
      
      if (recommendedGames.length === 0) {
        console.warn(`[Collab Recommender] Nenhum jogo encontrado após DatabaseUtils.executeSafeQuery. Usando fallback.`);
        return this.getFallbackRecommendations(count, excludeGameIds);
      }
      
      // Remover duplicatas (pode haver jogos recomendados por múltiplos usuários similares)
      // e limitar ao número desejado
      const uniqueGames = GameProcessingUtils.removeDuplicateGames(recommendedGames);
      
      if (uniqueGames.length < count) {
        console.warn(`[Collab Recommender] Apenas ${uniqueGames.length} jogos únicos encontrados. Considerando fallback para completar se necessário.`);
        // Poderia buscar mais jogos populares aqui para completar, ou deixar como está.
        // Por ora, retornamos o que temos até o limite.
      }
      
      return uniqueGames.slice(0, count);
    } catch (error) {
      console.error('[Collab Recommender] Erro ao gerar recomendações colaborativas:', error);
      return this.getFallbackRecommendations(count, excludeGameIds);
    }
  }
  
  /**
   * Calcula similaridade simples entre usuários
   */
  private async calculateSimpleUserSimilarity(userId: string): Promise<any[]> {
    try {
      // Obter jogos do usuário atual
      const { data: userGames, error: userGamesError } = await supabase
        .from('user_games')
        .select('game_id, rating')
        .eq('user_id', userId)
        .eq('owned', true);
        
      if (userGamesError || !userGames || userGames.length === 0) {
        console.error('Não foi possível obter jogos do usuário para cálculo de similaridade:', userGamesError);
        return [];
      }
      
      // Obter todos os usuários que possuem jogos em comum
      const userGameIds = userGames.map((g: { game_id: string; game?: Game | null }) => g.game_id);
      
      if (userGameIds.length === 0) {
        return [];
      }
      
      const MAX_IDS = 100; // Limite para operador IN
      let potentialSimilarUsers: any[] = [];
      
      // Dividir em lotes se necessário
      for (let i = 0; i < userGameIds.length; i += MAX_IDS) {
        const batch = userGameIds.slice(i, i + MAX_IDS);
        
        // Buscar outros usuários que possuem os mesmos jogos
        const { data: batchUsers, error: batchError } = await supabase
          .from('user_games')
          .select('user_id, game_id, rating')
          .in('game_id', batch)
          .neq('user_id', userId)
          .eq('owned', true);
          
        if (!batchError && batchUsers && batchUsers.length > 0) {
          potentialSimilarUsers = [...potentialSimilarUsers, ...batchUsers];
        }
      }
      
      // Agrupar por usuário
      const userGamesMap = new Map<string, { userId: string; games: Map<string, number> }>();
      
      potentialSimilarUsers.forEach(userGame => {
        if (!userGame.user_id || !userGame.game_id) return;
        
        if (!userGamesMap.has(userGame.user_id)) {
          userGamesMap.set(userGame.user_id, {
            userId: userGame.user_id,
            games: new Map<string, number>()
          });
        }
        
        const userInfo = userGamesMap.get(userGame.user_id);
        if (userInfo) {
          userInfo.games.set(userGame.game_id, userGame.rating || 0);
        }
      });
      
      // Mapear jogos do usuário atual (use Game)
      const currentUserGamesMap = new Map<string, { rating: number; game: Game }>();
      userGames.forEach((ug: { game_id: string; rating: number | null; game?: Game | null }) => {
        if (ug.game_id) {
          currentUserGamesMap.set(ug.game_id, {
            rating: ug.rating || 0,
            game: ug.game as Game
          });
        }
      });
      
      // Calcular similaridade
      const similarities: any[] = [];
      
      userGamesMap.forEach(otherUser => {
        // Contar jogos em comum e calcular similaridade
        let commonGames = 0;
        let totalGames = otherUser.games.size;
        
        otherUser.games.forEach((_, gameId) => {
          if (currentUserGamesMap.has(gameId)) {
            commonGames++;
          }
        });
        
        // Calcular similaridade como proporção de jogos em comum
        const similarity = commonGames / Math.max(1, totalGames);
        
        if (similarity >= 0.2) { // Limiar mínimo de similaridade
          similarities.push({
            userId: otherUser.userId,
            score: similarity
          });
        }
      });
      
      // Ordenar por similaridade (maior primeiro)
      return similarities.sort((a, b) => b.score - a.score);
    } catch (error) {
      console.error('Erro ao calcular similaridade entre usuários:', error);
      return [];
    }
  }
  
  /**
   * Obtém jogos populares para complementar recomendações se necessário
   * Retorna Game com _score adicionado.
   */
  protected async getAdditionalPopularGames(
    userId: string,
    count: number,
    excludeGameIds: string[],
    filters?: SharedRecommendationFilters
  ): Promise<Array<Game & { _score: number }>> {
    try {
      if (count <= 0) return [];
      
      // Montar o objeto de filtros para executeSafeQuery
      // Inclui os filtros passados, o critério de popularidade (gte_average_rating),
      // e os IDs a serem excluídos.
      const queryFilters: Record<string, any> = {
        ...(filters || {}), // Espalha os filtros de RecommendationFilters (complexidade, players, etc.)
        'gte_average_rating': RECOMMENDATION_CONFIG.recommenderThresholds.minPopularityRating,
        'excludeIds': excludeGameIds
      };
      
      // DatabaseUtils.executeSafeQuery já lida com as exclusões globais configuradas
      const popularGames = await DatabaseUtils.executeSafeQuery(
        queryFilters,
        count, // Buscar a quantidade desejada (ou um pouco mais se precisar de margem para algo)
        'average_rating', // Ordenar por avaliação média
        false // Descendente
        // Não há includeOnlyGameIds aqui, pois queremos jogos populares em geral
      );

      if (!popularGames || popularGames.length === 0) {
        console.warn('[Collab Recommender] Nenhum jogo popular adicional encontrado via executeSafeQuery.');
        return [];
      }
      
      // Adicionar o _score, que neste contexto de populares pode ser a própria average_rating
      // ou uma lógica de score mais simples se necessário.
      return popularGames.map(game => ({
         ...game,
          _score: game.average_rating || 0 // Usar average_rating como _score para populares
        }));
        
    } catch (error) {
      console.error('[Collab Recommender] Erro ao buscar jogos populares adicionais:', error);
      return [];
    }
  }
  
  /**
   * Atualiza a matriz de similaridade entre usuários
   */
  public async updateSimilarityMatrix(): Promise<void> {
    try {
      console.warn('Iniciando atualização da matriz de similaridade - operação pode ser demorada');
      
      // Obter todos os usuários da tabela profiles
      const { data: users, error: usersError } = await supabase
        .from('profiles')
        .select('id')
        .limit(1000); // Limitar para evitar sobrecarga
        
      if (usersError || !users || users.length === 0) {
        console.error('Erro ao obter lista de usuários para atualizar similaridade:', usersError);
        return;
      }
      
      console.log(`Encontrados ${users.length} usuários para cálculo de similaridade`);
      
      // Para cada usuário, calcular similaridade
      for (const user of users) {
        const similarities = await this.calculateSimpleUserSimilarity(user.id);
        
        // Salvar similaridades
        if (similarities.length > 0) {
          try {
            // Deletar similaridades antigas
            await supabase
              .from('user_similarities')
              .delete()
              .eq('user_id', user.id);
              
            // Inserir novas similaridades
            const { error: insertError } = await supabase
              .from('user_similarities')
              .insert(similarities.map(sim => ({
                user_id: user.id,
                similar_user_id: sim.userId,
                similarity_score: sim.score
              })));
              
            if (insertError) {
              console.error(`Erro ao salvar similaridades para o usuário ${user.id}:`, insertError);
            } else {
              console.log(`Salvas ${similarities.length} similaridades para o usuário ${user.id}`);
            }
          } catch (e) {
            console.error(`Erro ao atualizar similaridades para o usuário ${user.id}:`, e);
          }
        }
      }
      
      console.warn('Atualização da matriz de similaridade concluída');
    } catch (error) {
      console.error('Erro ao atualizar matriz de similaridade:', error);
    }
  }
  
  /**
   * Inicializa e atualiza dados de similaridade se necessário
   */
  public async initializeSimilarityData(): Promise<void> {
    try {
      console.log('Verificando dados de similaridade entre usuários...');
      
      // Verificar se já temos dados de similaridade
      const { data: existingData, error } = await supabase
        .from('user_similarities')
        .select('id')
        .limit(1);
        
      if (error) {
        console.warn('Erro ao verificar dados de similaridade existentes:', error);
        return;
      }
      
      if (!existingData || existingData.length === 0) {
        console.log('Nenhum dado de similaridade encontrado. Iniciando cálculo de similaridades...');
        // Atualizar similaridades apenas se não temos dados
        await this.updateSimilarityMatrix();
      } else {
        console.log('Dados de similaridade já existem.');
      }
    } catch (error) {
      console.error('Erro ao inicializar dados de similaridade:', error);
    }
  }
  
  /**
   * Sobrescreve o método de fallback da classe base.
   * Deve retornar Promise<Game[]> para corresponder ao tipo unificado.
   */
  protected async getFallbackRecommendations(
    count: number,
    excludeGameIds: string[]
  ): Promise<Game[]> {
     console.warn('[Collab Recommender] Usando fallback interno via getAdditionalPopularGames.');
     const gamesWithScore = await this.getAdditionalPopularGames('', count, excludeGameIds);
     return gamesWithScore.map(({ _score, ...game }) => game as Game);
   }
} 